import { FrontSide, ShaderMaterial, Vector2 } from 'three';

import { listen } from '@pkg/components/Sections/Profile/pubSub';
import { glTouchMoveTopic, glUpdateTopic } from '@pkg/components/Sections/Profile/pubSub/constants';

import { type EventData } from '../../globals/EventListeners';

import { blobs } from './blobs';
import { fragmentShader, vertexShader } from './shaders';

export class BackgroundMaterial extends ShaderMaterial {
    private _runIdleAnimation: boolean;

    constructor() {
        const uniforms = {
            aspectRatio: { value: window.innerWidth / window.innerHeight },
            resolution: { value: new Vector2(window.innerWidth, window.innerHeight) },
            mouse: { value: new Vector2() },
            time: { value: 0 },
            blobs: { value: blobs },
            brightness: { value: 1 },
            errorVisibility: { value: 0 },
        };

        super({
            vertexShader,
            fragmentShader,
            uniforms,
            transparent: false,
            depthTest: false,
            depthWrite: false,
            side: FrontSide,
            defines: {
                BLOB_COUNT: blobs.length,
            },
        });

        this._runIdleAnimation = true;

        listen(glUpdateTopic, this.handleUpdate.bind(this));
        listen(glTouchMoveTopic, this.handleMouseMove.bind(this));
    }

    public get runIdle(): boolean {
        return this._runIdleAnimation;
    }

    public set runIdle(value: boolean) {
        this._runIdleAnimation = value;
    }

    resize(width, height) {
        this.uniforms.resolution.value.x = width;
        this.uniforms.resolution.value.y = height;
        this.uniforms.aspectRatio.value = width / height;
    }

    public set error(value: boolean) {
        this.uniforms.errorVisibility.value = value ? 1 : 0;
    }

    remapNormalised(value: number) {
        return value * 0.5 + 0.5;
    }

    handleMouseMove(data: EventData) {
        const mousePos = data.normalized;
        this.uniforms.mouse.value.set(this.remapNormalised(mousePos.x), this.remapNormalised(mousePos.y));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    runIdleAnimation(elapsed: number, blobs: any) {
        blobs.value[0].position = new Vector2(
            this.remapNormalised(Math.sin(elapsed * 0.3)),
            Math.cos(elapsed * 0.3) * 0.5 + 0.5,
        );
        blobs.value[1].position = new Vector2(
            this.remapNormalised(-Math.sin(elapsed * 0.3)) * 0.5,
            this.remapNormalised(-Math.cos(elapsed * 0.3)),
        );
        blobs.value[2].position = new Vector2((this.remapNormalised(Math.cos(elapsed * 0.2)) * 1) / 2, -0.2);
        blobs.value[3].position = new Vector2(this.remapNormalised(Math.sin(elapsed * 0.2)), 1);
        blobs.value[4].position = new Vector2(
            this.remapNormalised(-Math.sin(elapsed * 0.2)) * 0.8,
            this.remapNormalised(Math.cos(elapsed * 0.2)) * 0.7,
        );
    }

    handleUpdate({ elapsed }: { elapsed: number }) {
        this.uniforms.time.value = elapsed;
        const { blobs } = this.uniforms;
        if (this.runIdle) {
            this.runIdleAnimation(elapsed, blobs);
        }
    }
}
